import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Palette, Link as LinkIcon, Hash, Type } from 'lucide-react';
import toast from 'react-hot-toast';
import { departmentService, themeColors} from './departmentService';

interface DepartmentFormData {
  name: string;
  theme_color: string;
  url: string;
  hashtags?: string;
  title?: string;
  description: string;
  order: number;
  is_active: boolean;
}

const DepartmentForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [previewTheme, setPreviewTheme] = useState<string>('blue');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<DepartmentFormData>({
    defaultValues: {
      name: '',
      theme_color: 'blue',
      url: '',
      hashtags: '',
      title: '',
      description: '',
      order: 1,
      is_active: true
    }
  });

  // Watch theme color for preview
  const selectedTheme = watch('theme_color', 'blue');
  
  useEffect(() => {
    setPreviewTheme(selectedTheme);
  }, [selectedTheme]);

  // Fetch existing data for edit
  useEffect(() => {
    if (isEdit && id) {
      fetchDepartment();
    }
  }, [id, isEdit]);

  const fetchDepartment = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getById(parseInt(id!));
      
      if (response.success) {
        const data = response.data;
        reset({
          name: data.name,
          theme_color: data.theme_color,
          url: data.url,
          hashtags: data.hashtags || '',
          title: data.title || '',
          description: data.description,
          order: data.order,
          is_active: data.is_active
        });
      } else {
        toast.error('Failed to fetch department');
        navigate('/admin/departments');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch department');
      navigate('/admin/departments');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: DepartmentFormData) => {
    console.log('Form data submitted:', data);
    
    try {
      setLoading(true);
      
      await departmentService.initializeCsrf();
      
      let response;
      if (isEdit && id) {
        console.log('Updating department ID:', id);
        response = await departmentService.update(parseInt(id), data);
      } else {
        console.log('Creating new department');
        response = await departmentService.create(data);
      }
      
      if (response.success) {
        toast.success(`Department ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/admin/departments');
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Submit error details:', error);
      
      if (error.response?.status === 419) {
        toast.error('CSRF token expired. Please refresh the page and try again.');
        await departmentService.initializeCsrf();
      } else if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key].join(', ')}`);
        });
      } else {
        toast.error(error.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPreviewClasses = () => {
    const theme = themeColors.find(t => t.value === previewTheme) || themeColors[0];
    return {
      bgColor: theme.bgClass,
      borderColor: theme.borderClass,
      textColor: theme.textClass,
      linkBg: theme.linkClass
    };
  };

  const preview = getPreviewClasses();

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/departments"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to Departments
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Edit Department' : 'Create New Department'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEdit ? 'Update department details' : 'Add a new department with theme customization'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Form Fields */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Department Information</h2>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department Name *
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: 'Department name is required' })}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="e.g., Enhance Health Services"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                {/* Theme Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Theme Color *
                  </label>
                  <div className="flex items-center space-x-4">
                    <Palette size={20} className="text-gray-400" />
                    <select
                      {...register('theme_color', { required: 'Theme color is required' })}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {themeColors.map(color => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    This determines the color scheme of the department card
                  </p>
                </div>

                {/* URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL / Route *
                  </label>
                  <div className="flex items-center space-x-4">
                    <LinkIcon size={20} className="text-gray-400" />
                    <input
                      type="text"
                      {...register('url', { required: 'URL is required' })}
                      className={`flex-1 px-4 py-3 rounded-lg border ${
                        errors.url ? 'border-red-300' : 'border-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="/health, /education, etc."
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    This will be the "more info" button link
                  </p>
                </div>

                {/* Title (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Title (Optional)
                  </label>
                  <div className="flex items-center space-x-4">
                    <Type size={20} className="text-gray-400" />
                    <input
                      type="text"
                      {...register('title')}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Health Services"
                    />
                  </div>
                </div>

                {/* Hashtags (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hashtags (Optional)
                  </label>
                  <div className="flex items-center space-x-4">
                    <Hash size={20} className="text-gray-400" />
                    <input
                      type="text"
                      {...register('hashtags')}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="#health #wellness"
                    />
                  </div>
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('order', { min: 0, valueAsNumber: true })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lower numbers appear first
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={6}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter department description..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                {/* Active Status */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register('is_active')}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Active (visible on website)
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Preview & Actions */}
          <div className="space-y-6">
            {/* Live Preview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Live Preview</h2>
              <div className={`p-4 rounded-lg ${preview.bgColor} border-l-4 ${preview.borderColor}`}>
                <h3 className={`text-lg font-semibold ${preview.textColor} mb-2`}>
                  {watch('name') || 'Department Name'}
                </h3>
                {watch('title') && (
                  <p className="text-sm text-gray-700 font-medium mb-1">{watch('title')}</p>
                )}
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {watch('description') || 'Department description will appear here...'}
                </p>
                <button
                  type="button"
                  className={`flex items-center justify-between w-fit gap-2 text-sm text-white ${preview.linkBg} font-bold px-3 py-1 uppercase rounded-full`}
                >
                  more info
                  <span className="relative w-5 h-5 flex items-center justify-center">
                    <span className="absolute flex h-full w-full animate-ping opacity-75">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                      </svg>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                    </svg>
                  </span>
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Preview updates as you type • Link goes to: <code className="bg-gray-100 px-1 rounded">{watch('url') || '/'}</code>
              </p>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Actions</h2>
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>{isEdit ? 'Update Department' : 'Create Department'}</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate('/admin/departments')}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Tips</h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Choose theme colors that match your branding
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  The URL determines where the "more info" button goes
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Keep descriptions concise but informative (2-3 sentences)
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Use hashtags for SEO and categorization
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2"></div>
                  Lower order numbers appear first in the grid
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DepartmentForm;